import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { cabinetDictApi, cabinetPersonApi, authApi, cabinetUsersApi, DictItem, MonitorChannel } from '../../services/api';
import { JuridicalType, PersonD } from '../../types';
import { formatAppDateTime } from '../../utils/dateFormat';
import PageHeader from '../../components/PageHeader';
import MobileEntityCard, { ActionBtn } from '../../components/MobileEntityCard';
import YandexOrdImportPanel from '../../components/YandexOrdImportPanel';
import { useAuth } from '../../contexts/AuthContext';

type AccessUser = {
  userId: number;
  login: string;
  inn?: string;
  personName?: string;
  allowSelfAd?: boolean;
  role?: string;
  accessCreatives?: boolean;
  accessPersons?: boolean;
  accessContracts?: boolean;
  accessInvoices?: boolean;
};

const defaultGrantPerms = {
  accessCreatives: true,
  accessPersons: false,
  accessContracts: false,
  accessInvoices: false,
};

function formatAccessSections(u: AccessUser): string {
  const parts: string[] = [];
  if (u.accessCreatives) parts.push('креативы');
  if (u.accessPersons) parts.push('контрагенты');
  if (u.accessContracts) parts.push('договоры');
  if (u.accessInvoices) parts.push('акты');
  return parts.length ? parts.join(', ') : '—';
}

const PersonsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isOwner, isClientAdmin, canPersons } = useAuth();
  const canManagePersons = isOwner || isClientAdmin;
  const isStartTariff = user?.tariff === 'START';
  const canImportFromOrd =
    !!user
    && user.ordProvider !== 'YANDEX'
    && !isStartTariff
    && canManagePersons;
  const [items, setItems] = useState<PersonD[]>([]);
  const [countries, setCountries] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accessCreds, setAccessCreds] = useState<{ login: string; password: string; inn: string; personName: string } | null>(null);
  const [accessList, setAccessList] = useState<AccessUser[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessName, setAccessName] = useState('');
  const [accessSaving, setAccessSaving] = useState(false);
  const [grantPerms, setGrantPerms] = useState({ ...defaultGrantPerms });
  const [grantTargetPerson, setGrantTargetPerson] = useState<PersonD | null>(null);

  const [channelPerson, setChannelPerson] = useState<PersonD | null>(null);
  const [channels, setChannels] = useState<MonitorChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelSaving, setChannelSaving] = useState(false);
  const [channelSyncing, setChannelSyncing] = useState(false);
  const [channelPlatform, setChannelPlatform] = useState<'YOUTUBE' | 'VK_VIDEO' | 'RUTUBE' | 'TELEGRAM'>('YOUTUBE');
  const [channelInput, setChannelInput] = useState('');

  const [limitsPerson, setLimitsPerson] = useState<PersonD | null>(null);
  const [weeklyLimitInput, setWeeklyLimitInput] = useState('');
  const [monthlyLimitInput, setMonthlyLimitInput] = useState('');
  const [limitsSaving, setLimitsSaving] = useState(false);

  const [name, setName] = useState('');
  const [foreign, setForeign] = useState(false);
  /** base type without foreign: JURIDICAL | IP | PHYSICAL */
  const [baseType, setBaseType] = useState<'JURIDICAL' | 'IP' | 'PHYSICAL'>('PHYSICAL');
  const [inn, setInn] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [foreignInn, setForeignInn] = useState('');
  const [foreignRegistrationNumber, setForeignRegistrationNumber] = useState('');
  const [country, setCountry] = useState<DictItem | null>(null);
  const [foreignEpaymentMethod, setForeignEpaymentMethod] = useState('');
  const [platformUrls, setPlatformUrls] = useState<string[]>(['']);
  /** START: одна роль; иначе можно обе */
  const [roleAdvertiser, setRoleAdvertiser] = useState(true);
  const [rolePublisher, setRolePublisher] = useState(false);
  const [startRole, setStartRole] = useState<'advertiser' | 'publisher'>('advertiser');

  const juridicalType: JuridicalType = useMemo(() => {
    if (!foreign) return baseType;
    if (baseType === 'PHYSICAL') return 'FOREIGN_PHYSICAL';
    return 'FOREIGN_JURIDICAL'; // IP also maps to foreign juridical for ORD
  }, [foreign, baseType]);

  const selectedRoles = (): string[] => {
    if (isStartTariff) return [startRole];
    const roles: string[] = [];
    if (roleAdvertiser) roles.push('advertiser');
    if (rolePublisher) roles.push('publisher');
    return roles;
  };

  const formatRoles = (roles?: string[]) => {
    if (!roles?.length) return '—';
    return roles
      .map((r) => (r === 'advertiser' ? 'рекламодатель' : r === 'publisher' ? 'издатель' : r))
      .join(', ');
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [persons, countryList] = await Promise.all([
        cabinetPersonApi.list(),
        cabinetDictApi.countries().catch(() => [] as DictItem[]),
      ]);
      setItems(persons);
      setCountries(countryList.filter((c) => c.code !== '643'));
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const loadAccess = async () => {
    if (!isOwner) return;
    setAccessLoading(true);
    try {
      setAccessList((await authApi.listAccess()) || []);
    } catch {
      // ignore
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canCreate = () => {
    if (!name.trim()) return false;
    if (!address.trim()) return false;
    if (isStartTariff) {
      // ok — startRole always set
    } else if (!roleAdvertiser && !rolePublisher) {
      return false;
    }
    if (foreign) {
      if (!country?.code) return false;
      if (!phone.trim() && !foreignEpaymentMethod.trim()) return false;
      if (baseType === 'PHYSICAL') return !!foreignInn.trim();
      return !!foreignRegistrationNumber.trim();
    }
    return !!inn.trim();
  };

  const resetForm = () => {
    setName('');
    setInn('');
    setPhone('');
    setAddress('');
    setForeignInn('');
    setForeignRegistrationNumber('');
    setCountry(null);
    setForeignEpaymentMethod('');
    setPlatformUrls(['']);
    setRoleAdvertiser(true);
    setRolePublisher(false);
    setStartRole('advertiser');
  };

  const create = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const urls = platformUrls.map((u) => u.trim()).filter(Boolean);
      const result = await cabinetPersonApi.create({
        name,
        inn: foreign ? undefined : inn,
        phone,
        address: address.trim(),
        juridicalType,
        roles: selectedRoles(),
        foreignInn: foreign && baseType === 'PHYSICAL' ? foreignInn : undefined,
        foreignRegistrationNumber: foreign && baseType !== 'PHYSICAL' ? foreignRegistrationNumber : undefined,
        foreignOksmCountryCode: foreign ? country?.code : undefined,
        foreignEpaymentMethod: foreign ? foreignEpaymentMethod : undefined,
        syncToOrd: true,
        platformUrls: urls.length ? urls : undefined,
      });
      const createdPads = result?.platformsCreated || 0;
      const warnings = result?.warnings || [];
      let msg = 'Контрагент создан и отправлен в ОРД';
      if (createdPads > 0) {
        msg += `. Создано площадок: ${createdPads}`;
      }
      if (warnings.length) {
        msg += `. Внимание: ${warnings.join('; ')}`;
      }
      setSuccess(msg);
      resetForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  const importFromOrd = async () => {
    try {
      setImporting(true);
      setError(null);
      setSuccess(null);
      const res = await cabinetPersonApi.importFromOrd();
      setSuccess(
        `Из ОРД: +${res.personsImported} / обновлено ${res.personsUpdated}`
        + (res.errors?.length ? `. Ошибки: ${res.errors.slice(0, 3).join('; ')}` : ''),
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка импорта из ОРД');
    } finally {
      setImporting(false);
    }
  };

  const grantAccess = async (p: PersonD) => {
    if (isClientAdmin && !isOwner) {
      if (!p.syncedToOrd) {
        setError('Сначала синхронизируйте контрагента с ОРД');
        return;
      }
      setGrantPerms({ ...defaultGrantPerms });
      setGrantTargetPerson(p);
      return;
    }
    if (!grantPerms.accessCreatives && !grantPerms.accessPersons && !grantPerms.accessContracts && !grantPerms.accessInvoices) {
      setError('Выберите хотя бы один раздел');
      return;
    }
    try {
      setError(null);
      const res = await authApi.grantAccess(p.personDId, grantPerms);
      setAccessCreds({
        login: res.login,
        password: res.password,
        inn: res.inn,
        personName: res.personName,
      });
      await loadAccess();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось выдать доступ');
    }
  };

  const confirmGrantCounterpartyAccess = async () => {
    if (!grantTargetPerson) return;
    if (!grantPerms.accessCreatives && !grantPerms.accessPersons && !grantPerms.accessContracts && !grantPerms.accessInvoices) {
      setError('Выберите хотя бы один раздел');
      return;
    }
    setAccessSaving(true);
    setError(null);
    try {
      const res = await cabinetUsersApi.create({
        personDId: grantTargetPerson.personDId,
        ...grantPerms,
      });
      setAccessCreds({
        login: res.login,
        password: res.password,
        inn: '',
        personName: res.personName || grantTargetPerson.name,
      });
      setSuccess(`Доступ для «${grantTargetPerson.name}»: видит только связанные с ним объекты`);
      setGrantTargetPerson(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось выдать доступ');
    } finally {
      setAccessSaving(false);
    }
  };

  const grantSelfAdAccess = async () => {
    if (!grantPerms.accessCreatives && !grantPerms.accessPersons && !grantPerms.accessContracts && !grantPerms.accessInvoices) {
      setError('Выберите хотя бы один раздел');
      return;
    }
    setAccessSaving(true);
    setError(null);
    try {
      const res = await authApi.grantSelfAdAccess(accessName.trim() || undefined, grantPerms);
      setAccessCreds({
        login: res.login,
        password: res.password,
        inn: res.inn,
        personName: res.personName,
      });
      setAccessName('');
      await loadAccess();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось выдать доступ');
    } finally {
      setAccessSaving(false);
    }
  };

  const revokeAccess = async (u: AccessUser) => {
    try {
      setError(null);
      await authApi.revokeAccess(u.userId);
      setAccessList((prev) => prev.filter((x) => x.userId !== u.userId));
      await loadAccess();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось отозвать доступ');
    }
  };

  const openChannels = async (p: PersonD) => {
    setChannelPerson(p);
    setChannelInput('');
    setChannelPlatform('YOUTUBE');
    setChannelsLoading(true);
    setError(null);
    try {
      setChannels(await cabinetPersonApi.listChannels(p.personDId));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось загрузить каналы');
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  };

  const closeChannels = () => {
    setChannelPerson(null);
    setChannels([]);
    setChannelInput('');
  };

  const addPersonChannel = async () => {
    if (!channelPerson || !channelInput.trim()) return;
    setChannelSaving(true);
    setError(null);
    try {
      await cabinetPersonApi.addChannel(channelPerson.personDId, {
        platform: channelPlatform,
        channelId: channelInput.trim(),
        enabled: true,
      });
      setChannelInput('');
      setChannels(await cabinetPersonApi.listChannels(channelPerson.personDId));
      setSuccess('Канал добавлен');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось добавить канал');
    } finally {
      setChannelSaving(false);
    }
  };

  const removePersonChannel = async (channelId: number) => {
    if (!channelPerson || !window.confirm('Удалить канал из списка?')) return;
    try {
      setError(null);
      await cabinetPersonApi.removeChannel(channelPerson.personDId, channelId);
      setChannels(await cabinetPersonApi.listChannels(channelPerson.personDId));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось удалить канал');
    }
  };

  const syncPersonChannels = async () => {
    if (!channelPerson) return;
    setChannelSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await cabinetPersonApi.syncChannels(channelPerson.personDId);
      setSuccess(
        `Собрано каналов: ${res.channelsProcessed}. Привязано креативов: ${res.matched} `
        + `(YouTube: ${res.linkedYoutube}, VK: ${res.linkedVk}, Rutube: ${res.linkedRutube ?? 0}, Telegram: ${res.linkedTelegram ?? 0}). Ещё ждут ссылок: ${res.stillAwaiting}`
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сбора и привязки');
    } finally {
      setChannelSyncing(false);
    }
  };

  const openLimits = (p: PersonD) => {
    setLimitsPerson(p);
    setWeeklyLimitInput(p.weeklyAdLimit != null ? String(p.weeklyAdLimit) : '');
    setMonthlyLimitInput(p.monthlyAdLimit != null ? String(p.monthlyAdLimit) : '');
    setError(null);
  };

  const closeLimits = () => {
    setLimitsPerson(null);
    setWeeklyLimitInput('');
    setMonthlyLimitInput('');
  };

  const parseLimitInput = (raw: string): number | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      throw new Error('Лимит должен быть целым числом ≥ 0, либо пустым');
    }
    return n;
  };

  const saveLimits = async () => {
    if (!limitsPerson) return;
    try {
      setLimitsSaving(true);
      setError(null);
      await cabinetPersonApi.updateAdLimits(limitsPerson.personDId, {
        weeklyAdLimit: parseLimitInput(weeklyLimitInput),
        monthlyAdLimit: parseLimitInput(monthlyLimitInput),
      });
      setSuccess(`Лимиты для «${limitsPerson.name}» сохранены`);
      closeLimits();
      await load();
    } catch (e: any) {
      setError(e?.message || e?.response?.data?.message || 'Не удалось сохранить лимиты');
    } finally {
      setLimitsSaving(false);
    }
  };

  const formatLimits = (p: PersonD): string => {
    const week =
      p.weeklyAdLimit != null
        ? `${p.weeklyAdUsed ?? 0}/${p.weeklyAdLimit} нед.`
        : null;
    const month =
      p.monthlyAdLimit != null
        ? `${p.monthlyAdUsed ?? 0}/${p.monthlyAdLimit} мес.`
        : null;
    if (!week && !month) return '—';
    return [week, month].filter(Boolean).join(' · ');
  };

  const personActions = (p: PersonD) => (
    <>
      <ActionBtn onClick={() => openChannels(p)}>Каналы</ActionBtn>
      {canPersons && (
        <ActionBtn onClick={() => openLimits(p)}>Лимиты</ActionBtn>
      )}
      {isOwner && (p.inn || p.foreignInn) && (
        <ActionBtn onClick={() => grantAccess(p)}>Дать доступ</ActionBtn>
      )}
      {isClientAdmin && !isOwner && p.syncedToOrd && (
        <ActionBtn onClick={() => grantAccess(p)}>Доступ контрагенту</ActionBtn>
      )}
      {canManagePersons && !p.syncedToOrd && (
        <ActionBtn onClick={() => cabinetPersonApi.sync(p.personDId).then(load)}>Sync</ActionBtn>
      )}
    </>
  );

  return (
    <Box>
      <PageHeader
        title="Контрагенты"
        description={<>Собственный контрагент в ОРД — <code>my</code>. Поля со * обязательны.</>}
        actions={
          canImportFromOrd ? (
            <Button variant="outlined" disabled={importing} onClick={importFromOrd}>
              {importing ? 'Импорт…' : 'Из ОРД'}
            </Button>
          ) : undefined
        }
      />
      {user?.ordProvider === 'YANDEX' && (
        <YandexOrdImportPanel entityType="persons" onDone={load} />
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}
      {importing && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Подтягиваем контрагентов из ОРД…
        </Alert>
      )}

      <Dialog open={!!grantTargetPerson} onClose={() => setGrantTargetPerson(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Доступ: {grantTargetPerson?.name || ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Контрагент увидит только связанные с ним объекты в выбранных разделах.
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={grantPerms.accessCreatives} onChange={(e) => setGrantPerms((p) => ({ ...p, accessCreatives: e.target.checked }))} />}
              label="Креативы"
            />
            <FormControlLabel
              control={<Checkbox checked={grantPerms.accessPersons} onChange={(e) => setGrantPerms((p) => ({ ...p, accessPersons: e.target.checked }))} />}
              label="Контрагенты"
            />
            <FormControlLabel
              control={<Checkbox checked={grantPerms.accessContracts} onChange={(e) => setGrantPerms((p) => ({ ...p, accessContracts: e.target.checked }))} />}
              label="Договоры"
            />
            <FormControlLabel
              control={<Checkbox checked={grantPerms.accessInvoices} onChange={(e) => setGrantPerms((p) => ({ ...p, accessInvoices: e.target.checked }))} />}
              label="Акты"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantTargetPerson(null)}>Отмена</Button>
          <Button variant="contained" disabled={accessSaving} onClick={confirmGrantCounterpartyAccess}>
            {accessSaving ? <CircularProgress size={20} /> : 'Создать доступ'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!accessCreds} onClose={() => setAccessCreds(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isClientAdmin && !isOwner ? 'Доступ контрагенту' : 'Доступ для менеджера'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {isClientAdmin && !isOwner
              ? 'Передайте логин и пароль контрагенту. Он увидит только связанные с ним креативы и разделы по галкам. Пароль больше не покажем.'
              : 'Передайте менеджеру логин и пароль. Пароль больше не покажем.'}
          </Typography>
          <Typography variant="body2"><b>Комментарий:</b> {accessCreds?.personName}</Typography>
          {accessCreds?.inn && (
            <Typography variant="body2"><b>ИНН:</b> {accessCreds.inn}</Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}><b>Логин:</b> {accessCreds?.login}</Typography>
          <Typography variant="body2"><b>Пароль:</b> {accessCreds?.password}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessCreds(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!limitsPerson} onClose={closeLimits} maxWidth="xs" fullWidth>
        <DialogTitle>
          Лимиты рекламы
          {limitsPerson ? `: ${limitsPerson.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Ограничение на создание креативов по договорам этого контрагента.
              Пустое поле — без лимита. Неделя: пн–вс (МСК), месяц — календарный.
            </Typography>
            {limitsPerson && (
              <Typography variant="body2">
                Сейчас: {limitsPerson.weeklyAdUsed ?? 0} за неделю, {limitsPerson.monthlyAdUsed ?? 0} за месяц
              </Typography>
            )}
            <TextField
              label="Не больше в неделю"
              type="number"
              value={weeklyLimitInput}
              onChange={(e) => setWeeklyLimitInput(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              placeholder="без лимита"
              fullWidth
            />
            <TextField
              label="Не больше в месяц"
              type="number"
              value={monthlyLimitInput}
              onChange={(e) => setMonthlyLimitInput(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              placeholder="без лимита"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLimits} disabled={limitsSaving}>Отмена</Button>
          <Button variant="contained" onClick={saveLimits} disabled={limitsSaving}>
            {limitsSaving ? <CircularProgress size={20} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {isOwner && (
        <Accordion disableGutters defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1">Доступы менеджеров</Typography>
              {!accessLoading && accessList.length > 0 && (
                <Chip label={accessList.length} size="small" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: { xs: 1.25, sm: 2 }, overflow: 'hidden' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Самореклама — для своего монтажёра. По ИНН — кнопка «Дать доступ» в строке контрагента.
              Отметьте разделы, к которым нужен доступ.
            </Typography>
            <FormGroup sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={<Checkbox checked={grantPerms.accessCreatives} onChange={(e) => setGrantPerms((p) => ({ ...p, accessCreatives: e.target.checked }))} />}
                label="Креативы"
              />
              <FormControlLabel
                control={<Checkbox checked={grantPerms.accessPersons} onChange={(e) => setGrantPerms((p) => ({ ...p, accessPersons: e.target.checked }))} />}
                label="Контрагенты"
              />
              <FormControlLabel
                control={<Checkbox checked={grantPerms.accessContracts} onChange={(e) => setGrantPerms((p) => ({ ...p, accessContracts: e.target.checked }))} />}
                label="Договоры"
              />
              <FormControlLabel
                control={<Checkbox checked={grantPerms.accessInvoices} onChange={(e) => setGrantPerms((p) => ({ ...p, accessInvoices: e.target.checked }))} />}
                label="Акты"
              />
            </FormGroup>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <TextField
                label="Комментарий"
                value={accessName}
                onChange={(e) => setAccessName(e.target.value)}
                size="small"
                fullWidth
                placeholder="Мой монтажёр"
              />
              <Button
                variant="contained"
                onClick={grantSelfAdAccess}
                disabled={accessSaving}
                sx={{ whiteSpace: 'nowrap', minWidth: 140 }}
              >
                {accessSaving ? <CircularProgress size={20} /> : 'Дать доступ'}
              </Button>
            </Stack>
            {accessLoading ? (
              <CircularProgress size={20} />
            ) : accessList.length > 0 ? (
              isMobile ? (
                <Stack spacing={1}>
                  {accessList.map((u) => (
                    <Paper key={u.userId} variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
                      <Stack spacing={0.75}>
                        <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                          {u.login}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {u.personName || '—'}
                          {' · '}
                          {u.allowSelfAd ? 'самореклама' : u.inn ? `ИНН ${u.inn}` : u.role === 'STAFF' ? 'сотрудник' : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                          Разделы: {formatAccessSections(u)}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => revokeAccess(u)}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Забрать
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Логин</TableCell>
                        <TableCell>Комментарий</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Разделы</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accessList.map((u) => (
                        <TableRow key={u.userId}>
                          <TableCell>{u.login}</TableCell>
                          <TableCell>{u.personName || '—'}</TableCell>
                          <TableCell>{u.allowSelfAd ? 'самореклама' : u.inn ? `ИНН ${u.inn}` : u.role === 'STAFF' ? 'сотрудник' : '—'}</TableCell>
                          <TableCell>{formatAccessSections(u)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" color="error" onClick={() => revokeAccess(u)}>
                              Забрать
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            ) : (
              <Typography variant="body2" color="text.secondary">Пока никому доступ не выдан</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {canManagePersons && (
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            required
            label="Название / ФИО *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={foreign}
                onChange={(e) => {
                  setForeign(e.target.checked);
                  if (e.target.checked && baseType === 'IP') {
                    setBaseType('JURIDICAL');
                  }
                }}
              />
            }
            label="Иностранный контрагент"
          />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <FormControl required sx={{ minWidth: 220 }} fullWidth>
              <InputLabel>Тип *</InputLabel>
              <Select
                label="Тип *"
                value={baseType}
                onChange={(e) => setBaseType(e.target.value as 'JURIDICAL' | 'IP' | 'PHYSICAL')}
              >
                <MenuItem value="PHYSICAL">Физическое лицо</MenuItem>
                <MenuItem value="JURIDICAL">Юридическое лицо</MenuItem>
                {!foreign && <MenuItem value="IP">ИП</MenuItem>}
              </Select>
            </FormControl>

            {!foreign ? (
              <TextField
                required
                label="ИНН *"
                value={inn}
                onChange={(e) => setInn(e.target.value)}
                sx={{ minWidth: 220 }}
                fullWidth
              />
            ) : baseType === 'PHYSICAL' ? (
              <TextField
                required
                label="Налоговый номер (аналог ИНН) *"
                value={foreignInn}
                onChange={(e) => setForeignInn(e.target.value)}
                fullWidth
              />
            ) : (
              <TextField
                required
                label="Регистрационный номер *"
                value={foreignRegistrationNumber}
                onChange={(e) => setForeignRegistrationNumber(e.target.value)}
                fullWidth
              />
            )}
          </Stack>

          {isStartTariff ? (
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Роль в ОРД *</FormLabel>
              <RadioGroup
                row
                value={startRole}
                onChange={(e) => setStartRole(e.target.value as 'advertiser' | 'publisher')}
              >
                <FormControlLabel value="advertiser" control={<Radio />} label="Рекламодатель" />
                <FormControlLabel value="publisher" control={<Radio />} label="Издатель" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                На «Старте» выберите одну роль. В договоре стороны подставятся сами: с рекламодателем
                вы — исполнитель, с издателем — заказчик. Посредник (Тренди) — в отдельном договоре.
              </Typography>
            </FormControl>
          ) : (
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Роль в ОРД *</FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={roleAdvertiser}
                      onChange={(e) => setRoleAdvertiser(e.target.checked)}
                    />
                  }
                  label="Рекламодатель"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rolePublisher}
                      onChange={(e) => setRolePublisher(e.target.checked)}
                    />
                  }
                  label="Издатель"
                />
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                В ОРД роль обязательна — укажите хотя бы одну.
              </Typography>
            </FormControl>
          )}

          <TextField
            required
            label="Адрес *"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            helperText="Адрес обязателен для ОРД (в том числе VK)"
          />

          {foreign && (
            <>
              <Alert severity="info">
                Одно из полей «Номер телефона» или «Номер электронного средства платежа» должно быть заполнено
              </Alert>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  label="Номер телефона"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Номер электронного средства платежа"
                  value={foreignEpaymentMethod}
                  onChange={(e) => setForeignEpaymentMethod(e.target.value)}
                  fullWidth
                />
              </Stack>
              <Autocomplete
                options={countries}
                value={country}
                onChange={(_, v) => setCountry(v)}
                getOptionLabel={(o) => (o ? `${o.name} (${o.code})` : '')}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter(
                    (o) => o.name.toLowerCase().includes(q) || o.code.includes(q)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Страна *"
                    placeholder="Начните вводить название или ОКСМ и выберите из списка"
                  />
                )}
              />
            </>
          )}

          {!foreign && (
            <TextField
              label="Телефон"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              sx={{ maxWidth: { sm: 360 } }}
              fullWidth
            />
          )}

          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
              Ссылки на площадки (необязательно)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Можете сразу добавить ссылки на площадки контрагента для создания «площадки» в ОРД,
              если это необходимо, заполнять ссылки не обязательно.
              Нужны ссылки именно на канал (например youtube.com/@name, vk.com/club…), а не на главную сайта.
            </Typography>
            <Stack spacing={1}>
              {platformUrls.map((url, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <TextField
                    label={`Ссылка на канал ${idx + 1}`}
                    value={url}
                    onChange={(e) => {
                      const next = [...platformUrls];
                      next[idx] = e.target.value;
                      setPlatformUrls(next);
                    }}
                    fullWidth
                    placeholder="https://www.youtube.com/@channel или https://vk.com/club…"
                  />
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={platformUrls.length <= 1 && !url.trim()}
                    onClick={() => {
                      if (platformUrls.length === 1) {
                        setPlatformUrls(['']);
                      } else {
                        setPlatformUrls(platformUrls.filter((_, i) => i !== idx));
                      }
                    }}
                    sx={{ whiteSpace: 'nowrap', minWidth: 40 }}
                  >
                    −
                  </Button>
                </Stack>
              ))}
              <Button
                variant="text"
                onClick={() => setPlatformUrls([...platformUrls, ''])}
                sx={{ alignSelf: 'flex-start' }}
              >
                + ещё ссылку
              </Button>
            </Stack>
          </Box>

          <Button
            variant="contained"
            disabled={saving || !canCreate()}
            onClick={create}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {saving ? <CircularProgress size={20} /> : 'Создать'}
          </Button>
        </Stack>
      </Paper>
      )}

      {loading ? (
        <CircularProgress />
      ) : isMobile ? (
        <Stack spacing={1.25}>
          {items.map((p) => (
            <MobileEntityCard
              key={p.personDId}
              title={p.name}
              subtitle={`#${p.personDId} · ${formatAppDateTime(p.createdTs)}`}
              chips={<ChipLike synced={p.syncedToOrd} />}
              meta={[
                { label: 'Тип', value: p.juridicalType },
                { label: 'Роль', value: formatRoles(p.roles) },
                { label: 'ИНН', value: p.inn || p.foreignInn || p.foreignRegistrationNumber || '—' },
                { label: 'Адрес', value: p.address || '—' },
                {
                  label: 'Страна',
                  value: p.foreignOksmCountryCode
                    ? (countries.find((c) => c.code === p.foreignOksmCountryCode)?.name
                      || p.foreignOksmCountryCode)
                    : 'Россия',
                },
                { label: 'Лимиты', value: formatLimits(p) },
              ]}
              actions={personActions(p)}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell>ИНН / аналог</TableCell>
                <TableCell>Адрес</TableCell>
                <TableCell>Страна</TableCell>
                <TableCell>ОРД</TableCell>
                <TableCell>Лимиты</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.personDId}>
                  <TableCell>{p.personDId}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.juridicalType}</TableCell>
                  <TableCell>{formatRoles(p.roles)}</TableCell>
                  <TableCell>{p.inn || p.foreignInn || p.foreignRegistrationNumber || '—'}</TableCell>
                  <TableCell>{p.address || '—'}</TableCell>
                  <TableCell>
                    {p.foreignOksmCountryCode
                      ? (countries.find((c) => c.code === p.foreignOksmCountryCode)?.name
                        || p.foreignOksmCountryCode)
                      : 'Россия'}
                  </TableCell>
                  <TableCell>{p.syncedToOrd ? 'да' : 'нет'}</TableCell>
                  <TableCell>{formatLimits(p)}</TableCell>
                  <TableCell>{formatAppDateTime(p.createdTs)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {personActions(p)}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!channelPerson} onClose={closeChannels} maxWidth="md" fullWidth>
        <DialogTitle>
          Каналы для парсинга
          {channelPerson ? `: ${channelPerson.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Добавьте YouTube, VK или Rutube канал контрагента — система соберёт ролики, найдёт erid в описании
              и автоматически проставит ссылки на креативы по договорам этого контрагента.
              Для разовых роликов ссылки по-прежнему можно ввести вручную на странице креативов.
            </Alert>

            {channelsLoading ? (
              <CircularProgress size={28} />
            ) : channels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Каналы не добавлены</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Платформа</TableCell>
                      <TableCell>Название</TableCell>
                      <TableCell>ID / ссылка</TableCell>
                      {canManagePersons && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {channels.map((ch) => (
                      <TableRow key={ch.id}>
                        <TableCell>
                          {ch.platform === 'YOUTUBE' ? 'YouTube'
                            : ch.platform === 'RUTUBE' ? 'Rutube'
                            : ch.platform === 'TELEGRAM' ? 'Telegram'
                            : 'VK'}
                        </TableCell>
                        <TableCell>{ch.name}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{ch.channelId}</TableCell>
                        {canManagePersons && (
                          <TableCell>
                            <Button size="small" color="error" onClick={() => removePersonChannel(ch.id)}>
                              Удалить
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {canManagePersons && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                <FormControl sx={{ minWidth: 140 }} fullWidth>
                  <InputLabel>Платформа</InputLabel>
                  <Select
                    label="Платформа"
                    value={channelPlatform}
                    onChange={(e) => setChannelPlatform(e.target.value as 'YOUTUBE' | 'VK_VIDEO' | 'RUTUBE' | 'TELEGRAM')}
                  >
                    <MenuItem value="YOUTUBE">YouTube</MenuItem>
                    <MenuItem value="VK_VIDEO">VK Video</MenuItem>
                    <MenuItem value="RUTUBE">Rutube</MenuItem>
                    <MenuItem value="TELEGRAM">Telegram</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Ссылка или ID канала"
                  placeholder={
                    channelPlatform === 'TELEGRAM'
                      ? 'https://t.me/channel или @channel (только публичные)'
                      : channelPlatform === 'RUTUBE'
                      ? 'https://rutube.ru/channel/18010012/ или 18010012'
                      : 'https://www.youtube.com/@channel или vk.com/club12345'
                  }
                  value={channelInput}
                  onChange={(e) => setChannelInput(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  disabled={channelSaving || !channelInput.trim()}
                  onClick={addPersonChannel}
                  sx={{ flexShrink: 0, minWidth: 120 }}
                >
                  {channelSaving ? <CircularProgress size={20} /> : 'Добавить'}
                </Button>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChannels}>Закрыть</Button>
          <Button
            variant="contained"
            disabled={channelSyncing || channels.length === 0}
            onClick={syncPersonChannels}
          >
            {channelSyncing ? <CircularProgress size={20} /> : 'Собрать и привязать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ChipLike: React.FC<{ synced: boolean }> = ({ synced }) => (
  <Typography
    component="span"
    variant="caption"
    sx={{
      px: 1,
      py: 0.25,
      borderRadius: 999,
      bgcolor: synced ? 'rgba(21, 128, 61, 0.12)' : 'rgba(91, 107, 117, 0.12)',
      color: synced ? 'success.main' : 'text.secondary',
      fontWeight: 700,
    }}
  >
    {synced ? 'в ОРД' : 'локально'}
  </Typography>
);

export default PersonsPage;
